import React, { useState, useEffect, useRef } from 'react';

const BannerWithSideImagesMobile = () => {
    const [activeSlide, setActiveSlide] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const videoRef = useRef(null);

    const bannerSlides = [
        {
            image: "assets/bannerhome/banner1.jpg",
            title: "Build Your Dream Project with Us Today!",
            text: "From residential to commercial spaces, we bring your vision to life with unmatched craftsmanship and attention to detail."
        },
        {
            image: "assets/bannerhome/interior.jpg",
            title: null,
            text: null
        },
        {
            image: "assets/bannerhome/banner2.webp",
            title: "Start Building Your Project Today!",
            text: "Partner with us for seamless project execution, transparent processes, and a commitment to excellence from start to finish."
        },
    ];

    const sideMedia = [
        "assets/images/banner/constructionsunset.jpg",
        "assets/portfolio/17.webp",
        "assets/interior/interior1.jpg"
    ];

    // Check if device is mobile
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => {
            window.removeEventListener('resize', checkIsMobile);
        };
    }, []);

    // Auto-advance slides
    useEffect(() => {
        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % bannerSlides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [bannerSlides.length]);

    // Handle video play/pause based on active slide
    useEffect(() => {
        if (videoRef.current) {
            if (activeSlide === 1 && !isHovered) {
                // On mobile, we need user interaction to play video
                if (!isMobile) {
                    videoRef.current.play().catch(error => {
                        console.log("Video autoplay prevented:", error);
                    });
                }
            } else {
                videoRef.current.pause();
            }
        }
    }, [activeSlide, isHovered, isMobile]);

    return (
        <div className=" flex md:hidden flex-col md:flex-row bg-blue-50 py-2 md:py-8 px-2 md:px-4">
            {/* Main content container with spacing */}
            <div className="flex flex-col md:flex-row bg-white rounded-xl md:rounded-2xl shadow-lg overflow-hidden max-w-7xl mx-auto w-full">
                {/* Left side - Banner slides */}
                <div className="relative h-64 sm:h-80 md:h-auto md:w-2/3 rounded-t-xl md:rounded-l-2xl overflow-hidden">
                    {bannerSlides.map((slide, index) => (
                        <div
                            key={index}
                            className={`absolute inset-0 bg-cover bg-center flex items-center transition-opacity duration-1000 ${activeSlide === index ? 'opacity-100' : 'opacity-0'}`}
                            style={{ backgroundImage: `url(${slide.image})` }}
                        >
                            {/* Conditional content rendering */}
                            {slide.title && (
                                <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
                                    <div className="max-w-2xl bg-blue-50/80 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-xl md:rounded-2xl shadow-lg border border-blue-100">
                                        <span className="text-blue-800 font-medium mb-2 sm:mb-3 block text-sm sm:text-base md:text-lg">
                                            Building the better future
                                        </span>
                                        <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#00A3A1] mb-4 sm:mb-6 leading-tight">
                                            {slide.title}
                                        </h1>
                                        <p className="text-[#063970] mb-4 sm:mb-6 md:mb-8 text-sm sm:text-base md:text-lg leading-relaxed">{slide.text}</p>
                                        <div className="button-wrapper">
                                            <a
                                                href="/our-services"
                                                className="inline-flex items-center font-semibold bg-[#00A3A1] text-white px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 rounded-lg hover:bg-[#008b8a] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 md:hover:-translate-y-1 text-sm sm:text-base"
                                            >
                                                Our Services
                                                <svg className="ml-2 h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                                                </svg>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Navigation dots */}
                    <div className="absolute bottom-3 sm:bottom-4 md:bottom-6 left-0 right-0 flex justify-center space-x-2 sm:space-x-3">
                        {bannerSlides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveSlide(index)}
                                className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${activeSlide === index ? 'bg-blue-50 scale-110 sm:scale-125 shadow' : 'bg-blue-100/70 hover:bg-blue-200'}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Right side - Media (images and video) */}
                <div className="md:w-1/3 flex flex-row md:flex-col h-32 sm:h-40 md:h-auto overflow-hidden mt-2 md:mt-0">
                    {sideMedia.map((media, index) => {
                        const isVideo = media.endsWith('.mp4') || media.endsWith('.webm') || media.endsWith('.ogg');

                        return (
                            <div
                                key={index}
                                className={`relative flex-1 transition-all duration-700 overflow-hidden group mx-1 sm:mx-2 md:mx-0 md:my-2 lg:my-3 ${activeSlide === index ? 'flex-grow rounded-xl md:rounded-2xl' : 'flex-grow-0 md:flex-grow rounded-lg md:rounded-xl'}`}
                                onMouseEnter={() => !isMobile && setIsHovered(true)}
                                onMouseLeave={() => !isMobile && setIsHovered(false)}
                                onClick={() => isMobile && setActiveSlide(index)}
                            >
                                {/* Overlay with subtle gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>

                                {/* Indicator for active slide */}
                                <div className={`absolute top-2 right-2 md:top-4 md:right-4 w-2 h-2 sm:w-3 sm:h-3 rounded-full z-20 transition-all duration-300 ${activeSlide === index ? 'bg-green-400 ring-1 md:ring-2 ring-blue-50' : 'bg-blue-50/70'}`}></div>

                                {isVideo ? (
                                    <div className="relative w-full h-full">
                                        <video
                                            ref={index === 1 ? videoRef : null}
                                            className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                        >
                                            <source src={media} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>

                                        {/* Play/Pause overlay button for video */}
                                        {!isMobile && (
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                                                <button
                                                    className="bg-blue-50/30 backdrop-blur-sm rounded-full p-2 sm:p-3 hover:bg-blue-50/40 transition-colors duration-300"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (videoRef.current) {
                                                            if (videoRef.current.paused) {
                                                                videoRef.current.play();
                                                            } else {
                                                                videoRef.current.pause();
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-800" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        className="w-full h-full bg-cover bg-center scale-105 group-hover:scale-100 transition-transform duration-700"
                                        style={{ backgroundImage: `url(${media})` }}
                                    />
                                )}

                                {/* Subtle border effect on hover */}
                                <div className="absolute inset-0 border-2 border-blue-100/0 group-hover:border-blue-100/30 transition-all duration-500 rounded-none group-hover:rounded-lg"></div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default BannerWithSideImagesMobile;